import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { useDownloadAssetsMutation } from '../services/assetApi';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { Typography } from '@/components/ui/typography';
import { downloadBlob } from '@/util/downloadFile';
import { track } from '@/services/telemetry';
import type { AssetSelection } from '../../../types/asset.types';
import { buildZipFileName } from './downloadNaming';

export interface DownloadAssetProps {
  setShowModel: (show: boolean) => void;
  selection: AssetSelection;
  hiddenPhotoUUIDs?: string[];
  /** "Address 1[ - Address 2]" — zip filename base. */
  zipNameBase: string;
  /** Fallback saved name for a single-image download when the backend
   * doesn't send Content-Disposition (old backend during rollout). */
  fallbackImageName?: string;
  /** Whether a watermark is on file for the account this download is for.
   *  The caller decides which account: the session's on /home, the
   *  property's on the internal workspace (DV-2849 PR 2). */
  watermarkAvailable: boolean;
}

const DownloadAsset = ({
  setShowModel,
  selection,
  hiddenPhotoUUIDs = [],
  zipNameBase,
  fallbackImageName,
  watermarkAvailable
}: DownloadAssetProps) => {
  const dispatch = useDispatch();
  const [isWatermark, setIsWatermark] = useState(false);
  const [is43, setIs43] = useState(false);
  const [imageSize, setImageSize] = useState<string>('');
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [downloadAssets] = useDownloadAssetsMutation();

  // A deliberately picked single photo, not Download All happening to find
  // one visible photo (DV-2763).
  const isSingleImage =
    selection.photoUUIDs?.length === 1 && !selection.isDownloadAll;
  // DV-2820: offered to everyone now that customers can hide photos themselves
  // — otherwise a customer's hidden photo silently vanishes from their ZIP with
  // no affordance to include it. Still self-hides when nothing is hidden.
  const showHiddenChoice = !isSingleImage && hiddenPhotoUUIDs.length > 0;

  const downloadAssetImage = (imgSize: string) => {
    // DV-2763: Download All on a set whose every photo is hidden has nothing
    // to send until the hidden ones are included. An empty list is not a
    // scope asset-service understands (it answers 500), so say so here.
    if (selection.photoUUIDs?.length === 0 && !includeHidden) {
      dispatch(
        showAlert({
          type: Variants.WARNING,
          message: showHiddenChoice
            ? 'Every photo here is hidden. Check "Include hidden photos?" to download them.'
            : 'There are no photos to download.'
        })
      );
      return;
    }
    setImageSize(imgSize);
    setIsLoader(true);
    downloadAssetAPICall(imgSize, isWatermark);
  };

  const downloadAssetAPICall = async (size: string, watermark: boolean) => {
    // Keep the modal open (and the chosen card's spinner running) until the
    // request settles. Announce success only after the blob actually arrives —
    // an optimistic pre-await toast produced a success-then-error sequence
    // whenever the download failed (e.g. the DV-2162 WAF block on "Download
    // All"), and the download is a direct blob, not a background/notify job.
    try {
      // Dedupe the merge: a checked hidden photo rides along in the
      // selection (DV-2820), so it can appear in both lists.
      const photoUUIDs =
        includeHidden && selection.photoUUIDs
          ? [...new Set([...selection.photoUUIDs, ...hiddenPhotoUUIDs])]
          : selection.photoUUIDs;
      const result = await downloadAssets({
        size,
        watermark,
        propertyUUID: selection.propertyUUID,
        orderUUID: selection.orderUUID,
        photoUUIDs,
        // Only send includeHidden when the checkbox is actually rendered,
        // otherwise the assetApi layer omits the key.
        includeHidden: showHiddenChoice ? includeHidden : undefined,
        // Omit (never '3:2') when off — absent means the backend streams the
        // stored files byte-for-byte; any value triggers crop/re-encode.
        aspectRatio: is43 ? '4:3' : undefined
      }).unwrap();
      if (result.isZip) {
        downloadBlob(
          result.blob,
          buildZipFileName({
            zipNameBase,
            sizeKey: size,
            is43,
            watermarked: watermark
          })
        );
      } else {
        // Backend names single images (it knows the true extension after a
        // crop re-encode); the fallback covers an old backend mid-rollout.
        downloadBlob(
          result.blob,
          result.suggestedFilename ?? fallbackImageName ?? 'photo.jpg'
        );
      }
      // DV-3757: which sizes and options people actually download, counted
      // once the file has arrived.
      track('assets-downloaded', {
        size,
        watermark,
        aspectRatio: is43 ? '4:3' : 'original',
        photoCount: photoUUIDs?.length ?? 0,
        byOrder: !photoUUIDs,
        isDownloadAll: Boolean(selection.isDownloadAll),
        includeHidden: showHiddenChoice && includeHidden
      });
      setIsWatermark(false);
      setIs43(false);
      setIncludeHidden(false);
      setIsLoader(false);
      setShowModel(false);
      dispatch(
        showAlert({
          type: Variants.SUCCESS,
          message: 'Assets downloaded successfully!'
        })
      );
    } catch (error: unknown) {
      // Leave the modal open so the user can retry (e.g. a different size).
      setIsLoader(false);
      dispatch(
        showAlert({
          type: Variants.ERROR,
          apiResponse: error
        })
      );
    }
  };

  return (
    <div className="flex flex-col pt-4 pb-4 px-6">
      {/* No background on the section itself: the modal surface is
          bg-background, and a section-level bg-card painted a visible slab
          past the cards in dark mode. The cards carry bg-card individually. */}
      <section className="w-full pb-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ResolutionCard
            label="Large"
            dimensions="3600w x 2400h"
            description="Print quality, full-sized photos that should be used in most PM software and syndication platforms"
            sizeKey="fullSize"
            isLoader={isLoader}
            imageSize={imageSize}
            onDownload={downloadAssetImage}
          />
          <ResolutionCard
            label="Medium"
            dimensions="1920w x 1280h"
            description="Optimized to work with most MLS systems or PM software that does not accept full-sized photos"
            sizeKey="1920w"
            isLoader={isLoader}
            imageSize={imageSize}
            onDownload={downloadAssetImage}
          />
          <ResolutionCard
            label="Small"
            dimensions="830w x 553h"
            description="Used when uploading to a software with file size limitations or posting on social media"
            sizeKey="830w"
            isLoader={isLoader}
            imageSize={imageSize}
            onDownload={downloadAssetImage}
          />
        </div>
      </section>
      <div className="pb-3">
        <Typography
          as="div"
          variant="small"
          className="uppercase tracking-wide text-muted-foreground"
        >
          Download Options
        </Typography>
        <div className="mt-4 flex flex-col gap-5">
          <OptionRow
            label="Watermark"
            ariaLabel="Apply Watermark"
            checked={isWatermark}
            onCheckedChange={setIsWatermark}
            disabled={!watermarkAvailable}
            tooltip={
              watermarkAvailable
                ? undefined
                : 'No watermark image is on file for your account.'
            }
            description={
              <>
                Maximize brand exposure and prevent fraud activity by placing a
                custom designed watermark on your photos. Don&apos;t have a
                watermark on file yet? Contact us at{' '}
                <a
                  href="mailto:support@planomatic.com"
                  className="text-primary underline"
                >
                  support@planomatic.com
                </a>{' '}
                to get started.
              </>
            }
          />
          <OptionRow
            label="4:3 aspect ratio"
            ariaLabel="Crop to 4:3 aspect ratio"
            checked={is43}
            onCheckedChange={setIs43}
            description="Crop downloaded photos to a 4:3 aspect ratio instead of the standard 3:2."
          />
          {showHiddenChoice && (
            <div className="flex items-center gap-3">
              <Checkbox
                id="include-hidden-download"
                checked={includeHidden}
                onCheckedChange={(checked) =>
                  setIncludeHidden(checked === true)
                }
                aria-label="Include hidden photos?"
              />
              <label
                htmlFor="include-hidden-download"
                className="text-sm font-semibold"
              >
                Include hidden photos?
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface OptionRowProps {
  label: string;
  description: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
  tooltip?: string;
}

// Toggle-left / label+description-right row per the DV-2347 design's
// DOWNLOAD OPTIONS section.
const OptionRow = ({
  label,
  description,
  checked,
  onCheckedChange,
  ariaLabel,
  disabled = false,
  tooltip
}: OptionRowProps) => {
  const control = (
    <Switch
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className="mt-0.5"
    />
  );
  return (
    <div className="flex items-start gap-3">
      {tooltip ? (
        <TooltipProvider>
          <Tooltip delayDuration={250}>
            <TooltipTrigger asChild>
              {/* span wrapper for two reasons: a disabled Switch swallows the
                  pointer events the tooltip listens for, and Radix Tooltip and
                  Radix Switch both stamp `data-state` — with asChild on the
                  Switch directly, Tooltip's would overwrite the toggle styling
                  (same clash as the AssetsActionBar select-all checkbox). */}
              <span className="inline-flex">{control}</span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        control
      )}
      <div>
        <Typography as="div" variant="body-semibold">
          {label}
        </Typography>
        <Typography
          as="div"
          variant="small"
          className="mt-1 text-muted-foreground"
        >
          {description}
        </Typography>
      </div>
    </div>
  );
};

// Typing-indicator ellipsis: three always-present dots whose brightness
// sweeps left-to-right (po-ellipsis keyframe in app.css, staggered by
// per-dot delays). aria-hidden — "Downloading" already announces the state.
const AnimatedEllipsis = () => (
  <span aria-hidden className="-ml-1">
    <span className="animate-[po-ellipsis_1.2s_ease-in-out_infinite]">.</span>
    <span className="animate-[po-ellipsis_1.2s_ease-in-out_0.2s_infinite]">
      .
    </span>
    <span className="animate-[po-ellipsis_1.2s_ease-in-out_0.4s_infinite]">
      .
    </span>
  </span>
);

interface ResolutionCardProps {
  label: string;
  dimensions: string;
  description: string;
  sizeKey: string;
  isLoader: boolean;
  imageSize: string;
  onDownload: (size: string) => void;
}

const ResolutionCard = ({
  label,
  dimensions,
  description,
  sizeKey,
  isLoader,
  imageSize,
  onDownload
}: ResolutionCardProps) => (
  <div className="group flex">
    <div className="flex w-full flex-col rounded-lg border border-transparent bg-card shadow-md transition-all hover:border-primary hover:shadow-lg">
      <div className="rounded-t-lg bg-accent p-3 group-hover:bg-primary group-hover:text-primary-foreground">
        <Typography
          as="div"
          variant="heading-md"
          className="font-medium group-hover:text-primary-foreground"
        >
          {label}
        </Typography>
        <Typography
          as="div"
          variant="heading-lg"
          className="font-semibold group-hover:text-primary-foreground"
        >
          {dimensions}
        </Typography>
      </div>
      <div className="flex flex-1 flex-col p-3 pt-4">
        <Typography as="p" variant="heading-md" className="px-2 font-normal">
          {description}
        </Typography>
        <div className="mt-auto flex justify-center px-2 pb-2 pt-6">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={isLoader && imageSize === sizeKey}
            onClick={() => onDownload(sizeKey)}
          >
            {isLoader && imageSize === sizeKey ? (
              <>
                <Loader2 className="animate-spin" />
                Downloading
                <AnimatedEllipsis />
              </>
            ) : (
              'Download'
            )}
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export default DownloadAsset;
